import { Turnstile } from "@marsidev/react-turnstile";
import type { JSX } from "react";
import { useEffect, useState } from "react";

import { fetchUnsubscribeInfo, submitUnsubscribe, type UnsubscribeInfo } from "@/lib/api";

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const CATEGORY_LABELS = {
  post_status: "Post status emails",
  city_notification: "City notification emails",
} as const;

type Status = "loading" | "found" | "already" | "submitting" | "success" | "error";

export function Unsubscribe({ token }: { token: string }): JSX.Element {
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    void (async () => {
      try {
        const d = await fetchUnsubscribeInfo(token, controller.signal);
        setInfo(d);
        setStatus(d.unsubscribed ? "already" : "found");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Could not load this unsubscribe link.");
        setStatus("error");
      }
    })();
    return () => controller.abort();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (SITE_KEY && !turnstileToken) return;
    setStatus("submitting");
    setError(null);
    try {
      await submitUnsubscribe(token, turnstileToken ?? "");
      setStatus("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setTurnstileToken(undefined);
      setCaptchaKey((k) => k + 1);
      setStatus("found");
    }
  };

  const label = info ? CATEGORY_LABELS[info.category] : "";

  return (
    <main className="relative z-10 flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md border border-white/15 bg-ink p-8">
        {status === "loading" && (
          <div>
            <h2 className="mb-2 font-anton text-3xl uppercase leading-none text-paper">
              Unsubscribe
            </h2>
            <p className="font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
              LOADING…
            </p>
          </div>
        )}

        {(status === "found" || status === "submitting") && (
          <form onSubmit={handleSubmit}>
            <h2 className="mb-2 font-anton text-3xl uppercase leading-none text-paper">
              Unsubscribe
            </h2>
            <p className="mb-6 font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
              ONE CLICK AND YOU'RE OUT.
            </p>
            <div className="mb-4">
              <p className="mb-1 font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
                EMAIL
              </p>
              <p className="break-words font-mono text-sm text-paper">{info?.email}</p>
            </div>
            <div className="mb-4">
              <p className="mb-1 font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
                LIST
              </p>
              <p className="font-mono text-sm text-paper">{label}</p>
            </div>
            {SITE_KEY && (
              <div className="mb-4">
                <Turnstile
                  key={captchaKey}
                  siteKey={SITE_KEY}
                  options={{ theme: "dark" }}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(undefined)}
                  onError={() => {
                    setTurnstileToken(undefined);
                    setCaptchaFailed(true);
                  }}
                />
              </div>
            )}
            {captchaFailed && (
              <p className="mb-4 font-mono text-[0.65rem] tracking-wider text-accent">
                Captcha failed to load - an ad blocker or browser shield may be blocking it.{" "}
                <button
                  type="button"
                  onClick={() => {
                    setCaptchaFailed(false);
                    setCaptchaKey((k) => k + 1);
                  }}
                  className="underline hover:text-paper"
                >
                  Retry
                </button>
              </p>
            )}
            {error && (
              <p className="mb-4 font-mono text-[0.65rem] tracking-wider text-accent">{error}</p>
            )}
            <button
              type="submit"
              disabled={status === "submitting" || (SITE_KEY ? !turnstileToken : false)}
              className="w-full bg-accent px-4 py-3 font-anton text-lg uppercase tracking-wide text-ink transition-opacity disabled:opacity-40"
            >
              {status === "submitting" ? "UNSUBSCRIBING…" : "UNSUBSCRIBE"}
            </button>
          </form>
        )}

        {status === "already" && (
          <div>
            <h2 className="mb-2 font-anton text-3xl uppercase leading-none text-paper">
              Already unsubscribed
            </h2>
            <p className="font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
              {info?.email.toUpperCase()} IS ALREADY OFF THE {label.toUpperCase()} LIST.
            </p>
          </div>
        )}

        {status === "success" && (
          <div>
            <h2 className="mb-2 font-anton text-3xl uppercase leading-none text-paper">
              You're unsubscribed
            </h2>
            <p className="font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
              YOU'VE BEEN UNSUBSCRIBED FROM {label.toUpperCase()} FOR {info?.email.toUpperCase()}.
            </p>
          </div>
        )}

        {status === "error" && (
          <div>
            <h2 className="mb-2 font-anton text-3xl uppercase leading-none text-paper">
              Link not valid
            </h2>
            <p className="font-mono text-[0.65rem] font-semibold tracking-wider text-paper-muted">
              THIS UNSUBSCRIBE LINK IS INVALID OR HAS EXPIRED.
            </p>
            {error && (
              <p className="mt-4 font-mono text-[0.65rem] tracking-wider text-accent">{error}</p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
